// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Button, Typography, useTheme } from '@mui/material';
import { ReactNode, forwardRef } from 'react';

interface Props {
  /**
   * An icon to display before the label.
   */
  icon: ReactNode;

  /**
   * A label for the navigation link.
   */
  label: string;

  /**
   * Optional end icon (e.g., dropdown arrow).
   */
  endIcon?: ReactNode;

  /**
   * Whether this navigation item is currently active.
   */
  isActive?: boolean;
}

/**
 * A link component that renders a sidebar navigation link.
 * Can also be used as a dropdown trigger when onClick is provided.
 */
export const NavigationLink = forwardRef<HTMLButtonElement, Props>(
  function NavigationLink(props, ref) {
    const theme = useTheme();
    const isActive = props.isActive ?? false;

    return (
      <Button
        ref={ref}
        size="small"
        endIcon={props.endIcon}
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: 2,
          height: '100%',
          textDecoration: 'none',
          borderRadius: 0,
          color: isActive
            ? theme.palette.primary.main
            : theme.palette.text.secondary,
          backgroundColor: isActive
            ? theme.palette.primary.light + '20'
            : 'transparent',
          justifyContent: 'flex-start',
          [theme.breakpoints.down('md')]: {
            justifyContent: 'center',
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            '& .MuiButton-endIcon': {
              display: 'none',
            },
          },
          borderTop: '1px solid transparent',
          borderBottom: '1px solid transparent',
          borderBottomColor: isActive
            ? theme.palette.primary.main
            : 'transparent',
          cursor: 'default',
          '& .MuiButton-endIcon': {
            color: isActive ? theme.palette.primary.main : 'inherit',
          },
          '&:hover': {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.light + '10',
          },
        }}
      >
        {props.icon}
        <Typography
          variant="subtitle2"
          paddingLeft={1}
          display={{ xs: 'none', md: 'block' }}
          fontWeight={isActive ? 600 : 500}
        >
          {props.label}
        </Typography>
      </Button>
    );
  }
);
